// api/consent.ts
/* eslint-disable @typescript-eslint/consistent-type-definitions */
export interface ConsentStatusResponse {
    customerId: string;
    email?: string | null;
    phone?: string | null;
    consent: {
        email?: { status: 'OPT_IN' | 'OPT_OUT' };
        sms?: { status: 'OPT_IN' | 'OPT_OUT' | 'PENDING' };
    };
}

export interface UpdateConsentPayload {
    customerId: string;
    email?: string;
    phone?: string;
    consents: Array<{
        channel: 'EMAIL' | 'SMS';
        action: 'OPT_IN' | 'OPT_OUT';
    }>;
}

export interface ConsentUpdateResponse {
    message: string;
    status: {
        sms?: 'success' | 'fail';
        email?: 'success' | 'fail';
    };
}

/** ---------- small HTTP helper (timeout + better errors) ---------- */

class ApiError extends Error {
    readonly status: number;
    readonly url: string;
    readonly body?: unknown;

    constructor(message: string, opts: { status: number; url: string; body?: unknown }) {
        super(message);
        this.name = 'ApiError';
        this.status = opts.status;
        this.url = opts.url;
        this.body = opts.body;
    }
}

const BASE_URL = process.env.REACT_APP_CONSENT_API_BASE_URL;
if (!BASE_URL) {
    // Fail early during boot to avoid mysterious empty URL calls
    // (Remove if you prefer silent no-ops)
    // eslint-disable-next-line no-console
    console.error('Missing REACT_APP_CONSENT_API_BASE_URL');
}

type FetchJsonOptions = RequestInit & { timeoutMs?: number };

const withTimeout = (timeoutMs = 10000) => {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeoutMs);
    return { signal: controller.signal, clear: () => clearTimeout(id) };
};

async function fetchJson<T>(input: RequestInfo | URL, init?: FetchJsonOptions): Promise<T> {
    const { timeoutMs, ...rest } = init ?? {};
    const { signal, clear } = withTimeout(timeoutMs);

    try {
        const res = await fetch(input, { ...rest, signal });

        // Try to parse JSON body safely (even on errors)
        let data: unknown = undefined;
        const text = await res.text().catch(() => '');
        if (text) {
            try {
                data = JSON.parse(text);
            } catch {
                // leave as text if not JSON
                data = text;
            }
        }

        if (!res.ok) {
            throw new ApiError(`Request failed (${res.status})`, {
                status: res.status,
                url: String(input),
                body: data,
            });
        }

        return data as T;
    } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
            throw new ApiError('Request timed out', { status: 0, url: String(input) });
        }
        throw err;
    } finally {
        clear();
    }
}

function buildUrl(base: string, path: string, params?: Record<string, string | number | undefined>) {
    const url = new URL(path.replace(/^\//, ''), base.endsWith('/') ? base : `${base}/`);
    if (params) {
        Object.entries(params).forEach(([k, v]) => {
            if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
        });
    }
    return url;
}
/** ---------- API functions ---------- */

export const getConsentStatus = async (
    customerId: string,
    token: string,
    timeoutMs = 60000
): Promise<ConsentStatusResponse> => {
    if (!BASE_URL) throw new Error('CONSENT BASE_URL not configured');
    const url = buildUrl(BASE_URL, '/consent/status', { customerId });

    const data = await fetchJson<ConsentStatusResponse>(url, {
        headers: { Authorization: `Bearer ${token}` },
        timeoutMs,
    });

    // (optional) runtime validation
    // return ConsentStatusSchema.parse(data);
    return data;
};

export const updateConsent = async (
    payload: UpdateConsentPayload,
    token: string,
    timeoutMs = 60000
): Promise<ConsentUpdateResponse> => {
    if (!BASE_URL) throw new Error('CONSENT BASE_URL not configured');
    const url = buildUrl(BASE_URL, '/consent/update');

    return fetchJson<ConsentUpdateResponse>(url, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        timeoutMs,
    });
};
